import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { MapPin, Calendar, Clock, ArrowLeft } from "lucide-react";
import { useArticle } from "../api/hooks/useArticle";
import { assetUrl } from "../api/client";
import FormattedContent from "../components/FormattedContent";
import useScrollToTop from "../hooks/useScrollToTop";
import {
  parseTranslations,
  getTranslation,
  getAvailableLanguages,
  getFirstCountryName,
} from "../api/utils/translations";
import useLanguages from "../api/hooks/useLanguages";

const BlogPost = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  const { t, i18n } = useTranslation();
  useScrollToTop(); // Scroll to top on navigation
  const { data: article, isLoading } = useArticle(id, { preview: isPreview });
  const { data: languagesData } = useLanguages();
  const availableTranslations = useMemo(() => {
    return parseTranslations(article?.translations);
  }, [article]);

  const defaultLang = useMemo(() => {
    const ui = i18n.language || "en-US";
    // Check if UI language is available in translations
    const availableLangs = getAvailableLanguages(article?.translations);
    // Default to site language if available, otherwise first available language
    return availableLangs.includes(ui) ? ui : availableLangs[0] || "en-US";
  }, [article?.translations, i18n.language]);

  const [selectedLang, setSelectedLang] = useState(defaultLang);

  // Update selectedLang when defaultLang changes (when article loads)
  useEffect(() => {
    setSelectedLang(defaultLang);
  }, [defaultLang]);

  const tr = useMemo(() => {
    return getTranslation(article?.translations, selectedLang);
  }, [article?.translations, selectedLang]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary hover:text-primary/90"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("blogPost.backToStories")}
          </Link>
          <div className="mt-6 text-gray-600">
            {t("blogPost.notFound", { defaultValue: "Article not found." })}
          </div>
        </div>
      </div>
    );
  }

  const imgId = article.cover_image;
  const imgSrc = imgId
    ? assetUrl(imgId, { width: 1200, height: 675, fit: "cover" })
    : undefined;
  const date = article.published_at
    ? new Date(article.published_at).toLocaleDateString()
    : "";
  const readTime = article.read_time || "";

  const title = tr?.title || t("common.untitled", { defaultValue: "Untitled" });
  const contentHtml = tr?.content || "";

  const authorJunction = Array.isArray(article.author)
    ? article.author[0]
    : null;
  const author = authorJunction?.Author_id;

  const languages = availableTranslations.map((tr) => {
    // Find the language data from languagesData
    const languageData = languagesData?.find(
      (lang) => lang.code === tr.languages_code
    );

    return {
      code: tr.languages_code, // Use full language code (e.g., "en-US")
      name: languageData?.name || tr.languages_code, // Use name from languagesData or fallback to code
    };
  });

  // Helper function to get author description in current language
  const getAuthorDescription = (author) => {
    if (!author?.translations) return null;

    const currentLang = selectedLang;
    const translation = author.translations.find(
      (tr) => tr.languages_code === currentLang
    );

    // Fallback to English if current language not found
    return (
      translation?.description ||
      author.translations.find((tr) => tr.languages_code === "en-US")
        ?.description
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary hover:text-primary/90"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("blogPost.backToStories")}
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article Header */}
        <article className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Hero Image */}
          <div className="aspect-video overflow-hidden bg-muted">
            {imgSrc ? (
              <img
                src={imgSrc}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                {t("common.noImage", { defaultValue: "No image" })}
              </div>
            )}
          </div>

          {/* Article Content */}
          <div className="p-8">
            {/* Meta Information */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-500 mb-6 gap-4">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <span className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span className="truncate">
                    {getFirstCountryName(article?.countries, "—", selectedLang)}
                  </span>
                </span>
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {date}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {readTime}
                </span>
              </div>

              {/* Article language switcher */}
              {languages.length > 1 && (
                <div className="flex items-center gap-1 sm:gap-2">
                  {languages.map((lng) => (
                    <Button
                      key={lng.code}
                      variant={
                        lng.code === selectedLang ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setSelectedLang(lng.code)}
                      className={`text-xs transition-all px-2 py-1 ${
                        lng.code === selectedLang
                          ? "bg-primary text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <span className="hidden sm:inline">{lng.name}</span>
                      <span className="sm:hidden">
                        {lng.code.split("-")[0].toUpperCase()}
                      </span>
                      {lng.code === selectedLang && (
                        <span className="ml-1 text-xs">✓</span>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {title}
            </h1>
            {tr?.excerpt && (
              <p className="text-lg sm:text-xl text-gray-600 mb-8">
                {tr.excerpt}
              </p>
            )}

            {/* Author */}
            {author && (
              <div className="flex flex-col sm:flex-row sm:items-center mb-8 p-4 bg-gray-50 rounded-lg gap-3 sm:gap-4">
                {author.avatar && (
                  <img
                    src={assetUrl(author.avatar, {
                      width: 96,
                      height: 96,
                      fit: "cover",
                    })}
                    alt={author.first_name || "Author"}
                    className="w-12 h-12 rounded-full mx-auto sm:mx-0"
                  />
                )}
                <div className="text-center sm:text-left">
                  <p className="font-semibold text-gray-900">
                    {author.first_name} {author.last_name}
                  </p>
                  {getAuthorDescription(author) && (
                    <p className="text-sm text-gray-500">
                      {getAuthorDescription(author)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Article Content */}
            {contentHtml && (
              <FormattedContent
                content={contentHtml}
                language={selectedLang}
                className="prose prose-lg max-w-none"
              />
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
